/**
 * Jornada de trabalho, bloqueios e disponibilidade.
 *
 * Horário de trabalho trafega como 'HH:mm' no fuso da barbearia — nunca como
 * instante. "Abro às 9h" é uma afirmação sobre o relógio da parede.
 */

import { z } from 'zod';
import {
  formatDurationLabel,
  MAX_SERVICES_PER_APPOINTMENT,
  serviceIdsQuerySchema,
} from './catalog.js';
import { calendarDateSchema, idSchema, instantSchema, timeOfDaySchema } from './common.js';

export const weekdaySchema = z
  .number()
  .int()
  .min(0, 'Dia da semana inválido')
  .max(6, 'Dia da semana inválido');

export const WEEKDAY_LABELS = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
] as const;

export const WEEKDAY_SHORT_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

export const workingHoursRangeSchema = z
  .object({
    weekday: weekdaySchema,
    start: timeOfDaySchema,
    end: timeOfDaySchema,
  })
  .refine((range) => range.end > range.start, {
    message: 'O fim precisa ser depois do início',
    path: ['end'],
  });

/**
 * A jornada chega inteira, substituindo a anterior. Editar dia a dia por PATCH
 * criaria estados intermediários inconsistentes — e o barbeiro edita a semana
 * toda de uma vez mesmo.
 */
export const setWorkingHoursSchema = z.object({
  ranges: z.array(workingHoursRangeSchema).max(50, 'Jornada complexa demais'),
});

export interface WorkingHoursView {
  weekday: number;
  start: string;
  end: string;
}

// ── Bloqueios ───────────────────────────────────────────────────────────────

export const createTimeOffSchema = z
  .object({
    startsAt: instantSchema,
    endsAt: instantSchema,
    reason: z.string().trim().max(200).optional(),
    /** Sem profissional, o bloqueio fecha a barbearia inteira (feriado). */
    membershipId: idSchema.nullable().optional(),
  })
  .refine((input) => new Date(input.endsAt) > new Date(input.startsAt), {
    message: 'O fim precisa ser depois do início',
    path: ['endsAt'],
  });

export interface TimeOffView {
  id: string;
  startsAt: string;
  endsAt: string;
  reason: string | null;
  membershipId: string | null;
  membershipName: string | null;
}

// ── Disponibilidade ─────────────────────────────────────────────────────────

export const availabilityQuerySchema = z
  .object({
    date: calendarDateSchema,
    /**
     * Os serviços do atendimento. A duração que a agenda precisa acomodar é a
     * soma deles — "cabelo e barba" não procura dois buracos de 20 minutos,
     * procura um de 40.
     */
    serviceIds: serviceIdsQuerySchema.optional(),
    /** O nome antigo do parâmetro, de quando era um serviço só. */
    serviceId: idSchema.optional(),
    /** Opcional: sem profissional escolhido, a API responde por todos. */
    membershipId: idSchema.optional(),
    /**
     * Quando informado, a duração usada é a personalizada deste cliente — é o
     * que faz a lista de horários do João ser diferente da do Pedro.
     */
    clientId: idSchema.optional(),
    /**
     * Sobrepõe a duração calculada. É o caso do barbeiro digitando outro tempo
     * na hora de marcar ("hoje o cabelo dele vai demorar mais") — a lista de
     * horários precisa acompanhar o que ele acabou de digitar. Com vários
     * serviços é o total, já somado pela tela.
     */
    durationMinutes: z.coerce.number().int().min(5).max(480).optional(),
    /**
     * Minutos por serviço, na mesma ordem de `serviceIds`.
     *
     * Existe para a lista de horários responder ao que o barbeiro digitou item
     * a item. Sem isso, ele corrigiria a barba para 30 minutos e a tela ainda
     * ofereceria "dá para fazer só a barba (15 min)" — a mensagem citaria um
     * tempo que ele acabou de dizer que está errado.
     */
    durations: z
      .union([z.string(), z.array(z.string())])
      .transform((value) =>
        (Array.isArray(value) ? value : value.split(','))
          .map((entry) => Number(entry.trim()))
          .filter((entry) => Number.isFinite(entry)),
      )
      .pipe(z.array(z.number().int().min(5).max(480)).max(MAX_SERVICES_PER_APPOINTMENT))
      .optional(),
    /** Quantos dias varrer a partir de `date`. */
    days: z.coerce.number().int().min(1).max(31).default(1),
  })
  .transform(({ serviceId, serviceIds, ...rest }) => ({
    ...rest,
    // Um nome só daqui para dentro. O singular continua entrando porque o app
    // que está aberto no celular do barbeiro no momento da publicação ainda
    // pede assim — e "escolha ao menos um serviço" numa tela que tem um
    // serviço escolhido seria um erro impossível de entender.
    serviceIds: serviceIds ?? (serviceId ? [serviceId] : []),
  }))
  .refine((value) => value.serviceIds.length > 0, {
    message: 'Escolha ao menos um serviço',
    path: ['serviceIds'],
  })
  .refine(
    (value) => value.durations === undefined || value.durations.length === value.serviceIds.length,
    {
      // Posicional: sem o mesmo tamanho não há como saber qual tempo é de qual
      // serviço, e adivinhar aqui sairia como um horário errado na tela.
      message: 'Informe um tempo para cada serviço',
      path: ['durations'],
    },
  );

export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;

export interface AvailableSlotView {
  startsAt: string;
  endsAt: string;
  overlapMinutes: number;
}

export type UnavailableReason =
  | 'DAY_OFF'
  | 'BLOCKED'
  | 'PAST_DATE'
  | 'BEYOND_HORIZON'
  | 'FULLY_BOOKED';

export const UNAVAILABLE_MESSAGES: Record<UnavailableReason, string> = {
  DAY_OFF: 'Não há atendimento neste dia',
  BLOCKED: 'Agenda bloqueada neste dia',
  PAST_DATE: 'Esta data já passou',
  BEYOND_HORIZON: 'Ainda não é possível agendar tão longe',
  FULLY_BOOKED: 'Nenhum horário livre para este serviço',
};

/**
 * Por que este dia não serve — com o tempo que faltou, quando é o caso.
 *
 * "Nenhum horário livre" não diz à pessoa o que fazer; "nenhum horário de 55
 * min neste dia" diz, porque ela sabe que escolheu três serviços e pode tirar
 * um. A diferença só existe em `FULLY_BOOKED`: num domingo o tempo é
 * irrelevante, a barbearia está fechada.
 */
export function describeUnavailable(input: {
  reason?: UnavailableReason;
  durationMinutes: number;
}): string {
  if (!input.reason) return 'Sem horários neste dia';
  if (input.reason !== 'FULLY_BOOKED') return UNAVAILABLE_MESSAGES[input.reason];

  return `Nenhum horário de ${formatDurationLabel(input.durationMinutes)} neste dia`;
}

/**
 * O que ainda caberia no dia, quando a escolha inteira não coube.
 *
 * Vem do servidor — e não de uma conta feita na tela — porque a única forma
 * honesta de responder "cabe?" é a mesma que gera os horários. Uma estimativa
 * feita no app acertaria nos dias vazios e erraria nos apertados, que são
 * exatamente os dias em que esta mensagem aparece.
 */
export interface FittableSubsetView {
  /** Subconjunto dos serviços pedidos, na ordem em que chegaram. */
  serviceIds: string[];
  durationMinutes: number;
}

export interface DayAvailabilityView {
  date: string;
  membershipId: string;
  membershipName: string;
  /** Soma dos serviços pedidos — o tamanho do buraco procurado. */
  durationMinutes: number;
  /** Cada serviço com o tempo que ele leva para este cliente. */
  services: AvailabilityServiceView[];
  slots: AvailableSlotView[];
  reason?: UnavailableReason;
  /**
   * Preenchido só quando `slots` vem vazio e algum recorte da escolha caberia.
   * Nulo quando nem o serviço mais curto cabe — aí não há o que oferecer.
   */
  fits: FittableSubsetView | null;
}

export interface AvailabilityServiceView {
  id: string;
  name: string;
  /** Minutos deste cliente neste serviço, não o padrão do cadastro. */
  durationMinutes: number;
  priceCents: number;
}
